import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import BorderBoxBody from "@src/sharedComponents/BorderBox/BorderBoxBody";
import BorderBoxHeader from "@src/sharedComponents/BorderBox/BorderBoxHeader";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import Legend from "./Legend";
import ProjectionGraph, { GraphDataPoint } from "./ProjectionGraph";

import styles from "./Container.module.css";

interface Props {
  data: GraphDataPoint[];
}

const FinancialPlansProjectionGraphContainer: React.FC<Props> = ({ data }) => {
  return (
    <BorderBox className={styles.container}>
      <BorderBoxHeader
        className={styles.header}
        start={
          <div className={styles.flexCol}>
            <Text variant={"heading1"} color={"text"}>
              Projected Net Worth
            </Text>

            <Text variant="body" color={"textSubtle"}>
              Your projected net worth over time based on this plan
            </Text>
          </div>
        }
        end={
          <div className={styles.flexRow}>
            <Text variant="body" color={"textSubtle"}>
              How is this chart calculated?
            </Text>
            <Tooltip
              tooltipText={
                <div className={styles.tooltipText}>
                  <span>
                    This is a projection of your net worth over time. We
                    calculate it by estimating the rate of return and volatility
                    of your assets. We then factor in your cash flow and
                    liabilities to create a distribution of your possible net
                    worth over time.
                  </span>
                  <br />
                  <br />
                  <span>
                    Each line (High, Median, and Low) represents a probability
                    of reaching that net worth based on the assumptions in the
                    projections:
                  </span>
                  <ul className={styles.list}>
                    <li>
                      The Median Case represents a 50% probability of achieving
                      that net worth or higher;
                    </li>
                    <li>
                      The High Case represents the Median Case plus one standard
                      deviation (~16% probability of achieving that net worth or
                      higher); and
                    </li>
                    <li>
                      The Low Case represents the Median Case minus one standard
                      deviation (~84% probability of achieving that net worth or
                      higher).
                    </li>
                  </ul>
                </div>
              }
              placement={"top"}
            >
              <InfoOutlinedIcon className={styles.chartTooltipIcon} />
            </Tooltip>
          </div>
        }
      />

      <BorderBoxBody className={styles.borderBoxBody}>
        <Legend />

        <ProjectionGraph graphHeight={310} data={data} />

        <Text className={styles.xAxisLabel} variant={"bodySmall"}>
          Year
        </Text>

        <Text className={styles.yAxisLabel} variant={"bodySmall"}>
          Projected Net Worth
        </Text>
      </BorderBoxBody>
    </BorderBox>
  );
};

export default FinancialPlansProjectionGraphContainer;
